import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MatSnackBar, MAT_DIALOG_DATA } from "@angular/material";
import { IRol, IUser, RoleUser } from "../../models/vk-users.model";
import { VkUsersService } from "../../services/vk-users.service";

@Component({
  selector: 'vk-users-modal',
  templateUrl: 'vk-users.modal.html',
  styleUrls: ['vk-users.modal.scss']
})
export class VkUsersModalComponent implements OnInit {
  public formGroup: FormGroup;
  user?: IUser;
  roles: IRol[] = [];
  _roles: IRol[] = [];
  rolesChecked?: IRol[] = [];
  title: string;
  loading = false;
  mode: string = 'view';
  displayedColumns;
  readOnly: boolean = true;
  hide = true;
  loadingRoles = true;
  concatenatedNameRoles: string;
  
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<VkUsersModalComponent>,
    private usersService: VkUsersService,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.displayedColumns = ['checkbox', 'name', 'description'];
    if (this.data.user) {
      this.user = this.data.user;
      this.title = this.user.UserName;
    } else {
      this.user = { UserId: '00000000-0000-0000-0000-000000000000', UserName: null, Email: null, Password: null, Comment: null, CreateDate: null, LastActivityDate: null, IsApproved: null, IsLockedOut: null };
      this.title = 'Nuevo Usuario';
      this.mode = 'create';
    }
    this.getDataRoles();
    this.initForm();
    this.initInputs();
  }

  getDataRoles() {
    if (this.user) {
      this.usersService.getRolesForUser(this.user.UserId).subscribe(
        resp => this.getRolesResponseHandler(resp),
        () => this.getRolesResponseHandlerError()
      );
    }
  }

  getRolesResponseHandler(response){
    if (!response.success) { 
      this.loading = false;
      this.loadingRoles = false;
      this.showErrorMessage("ERROR: " + response.errorsMessages[0]);
      return;
    }
    this.roles = response.data.sort((a, b) => a.RoleName.localeCompare(b.RoleName));
    this._roles = this.roles.filter(x => x.HasIt);
    for (let role of this._roles) {
      this.rolesChecked.push(role);
    }
    this.loading = false;
    this.loadingRoles = false;
    if (this.mode === 'create') this.onCreate();
  }

  getRolesResponseHandlerError(){
    this.loading = false;
    this.loadingRoles = false;
    this.showErrorMessage("Ha ocurrido un error inesperado. Por favor, intente más tarde");
  }

  closeDialog() {
    this.dialogRef.close();
  }

  onCreate() {
    this.onCreateRolesUser();
  }

  onCreateRolesUser() {
    this.readOnly = false;
    this._roles = this.roles;
    
    let indexVKP3Orders = this._roles.findIndex(x => x.RoleName === 'VKP3Orders');
    if (indexVKP3Orders !== -1) this._roles.splice(indexVKP3Orders, 1);

    let indexVKP3Reports = this._roles.findIndex(x => x.RoleName === 'VKP3Reports');
    if (indexVKP3Reports !== -1)  this._roles.splice(indexVKP3Reports, 1);
  }

  onEdit() {
    this.mode = 'edit';
    this.onEditRolesUser();
  }

  onEditRolesUser() {
    this.readOnly = false;
    this._roles = this.roles;
  }

  onRolesChecked(roleId: string) {
    let index = this.rolesChecked.findIndex(x => x.RoleId === roleId);
    if (index !== -1) this.rolesChecked.splice(index, 1);
    else this.rolesChecked.push(this.roles.find(x => x.RoleId === roleId));
  }

  private initForm() {
    this.formGroup = this.formBuilder.group({
      UserId: [this.user.UserId, ],     
      UserName: ['', [Validators.required, Validators.maxLength(256)]],
      Email: ['', [, Validators.maxLength(256)]],
      Password: ['', [, Validators.maxLength(128)]],
      ConfirmPassword: ['', [, Validators.maxLength(128)]],
      Comment: ['', [, Validators.maxLength(256)]],
    }, { validator: this.checkIfMatchingPasswords('Password', 'ConfirmPassword')});
  }

  initInputs() {
    if (!this.user) { return;  }
    this.formGroup = this.formBuilder.group({
      UserId: [this.user.UserId, ],     
      UserName: [this.user.UserName, [Validators.required, Validators.maxLength(256)]],
      Email: [this.user.Email, [, Validators.maxLength(256)]],
      Password: ['', [, Validators.maxLength(128)]],
      ConfirmPassword: ['', [, Validators.maxLength(128)]],
      Comment: [this.user.Comment, [, Validators.maxLength(256)]],
    }, { validator: this.checkIfMatchingPasswords('Password', 'ConfirmPassword')});    
  }

  checkIfMatchingPasswords(passwordKey: string, passwordConfirmationKey: string) {
    return (group: FormGroup) => {
      let passwordInput = group.controls[passwordKey],
          passwordConfirmationInput = group.controls[passwordConfirmationKey];
      if (passwordInput.value !== passwordConfirmationInput.value) {
        return passwordConfirmationInput.setErrors({notEquivalent: true})
      }
      else {
          return passwordConfirmationInput.setErrors(null);
      }
    }
  }

  onRolesUpdate() {
    let rolesName = [];
    for (let role of this.rolesChecked) {
      rolesName.push(role.RoleName);
    }
    this.concatenatedNameRoles = ';' + rolesName.join(';') + ';';
  }

  onSearch(event: any) {
    let roles = this.roles.filter(x => x.RoleName.toLowerCase().includes(event.toLowerCase()) 
    || (x.Description || '').toLowerCase().includes(event.toLowerCase()));
    this._roles = roles.sort((a, b) => a.RoleName.localeCompare(b.RoleName));
  }

  onSubmit() {
    this.loading = true;
    if (!this.formGroup.valid) {
      this.loading = false;
      return ;
    }
    this.onRolesUpdate();
    let body = this.formGroup.getRawValue();
    if (this.mode === 'edit') {
      this.usersService.editUser(body, this.concatenatedNameRoles).subscribe(
        resp => this.editUserResponseHandler(resp),
        () => this.editUserResponseHandlerError());
    } else { 
      this.usersService.newUser(body, this.concatenatedNameRoles).subscribe(
        resp => this.newUserResponseHandler(resp),
        () => this.newUserResponseHandlerError());
    }  
  }

  newUserResponseHandler(response){
    if (!response.success) { 
      this.loading = false;
      this.showErrorMessage("ERROR: " + response.errorsMessages[0]);
      return;
    }
    this.loading = false;
    this.closeDialog();
    this.snackBar.open("El Usuario ha sido creado con éxito", 'OK',{ duration: 4000 });
  }

  newUserResponseHandlerError(){
    this.loading = false;
    this.showErrorMessage("Ocurrió un error durante la creación del Usuario");
  }

  editUserResponseHandler(response){
    if (!response.success) { 
      this.loading = false;
      this.showErrorMessage("ERROR: " + response.errorsMessages[0]);
      return;
    }
    this.loading = false;
    this.closeDialog();
    this.snackBar.open("El Usuario ha sido editado con éxito", 'OK',{ duration: 4000 });
  }

  editUserResponseHandlerError() {
    this.loading = false;
    this.showErrorMessage("Ocurrió un error durante la edición del Usuario");
  }

  private showErrorMessage(message: string) { 
    this.snackBar.open(message, null, {duration: 5000});
  }
}
